import React, { useState } from 'react';
import { Form, Input, Button, Modal, Select, message, Card, AutoComplete, Tag, Checkbox, Row, Col, Tooltip, List, Space, DatePicker, Dropdown } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, PrinterOutlined, MoreOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from '../offline/profileApi';
import { isProfileQueued } from '../offline/profileActions';
import API from '../config/api';
import dayjs from 'dayjs';
import { sendReceiptNotificationToPatient } from '../services/notificationService';
import { useDoctorContext } from './DoctorContext';
import { useClinicContext } from './ClinicContext';
import { useInventoryContext } from './InventoryContext';
import { useLanguage } from './LanguageContext';
import { useDoctorSuggestions } from '../hooks/useDoctorSuggestions';
import { FetalAssessment, ObgynHistory } from './type';
import { openPrintWindow } from './printUtils';
import { getClinicOwnerDoctorId } from '../utils/clinicOwnerDoctor';

const { Option } = Select;
const { TextArea } = Input;

// Fixed investigation items for OB/GYN template (matching physical prescription paper)
const OBGYN_INVESTIGATIONS = [
  'cbc', 'pt.ptt.INR', 'RBS', 'FBS', 'pps',
  'Urine', 'HCV', 'HbSAg', 'Abo &RH', 'Tsh',
  'T3', 'T4', 'Fsh', 'Lh', 'Pri',
  'AMH', 'MTHFR',
  '1gm Lupus anticoag', 'Anticardiolpin gm', 'Factor v (pcr) &II', 'Ptn.s', 'Ptn.c',
  'Homocystiene', 'ANA.c4.c3', 'BHCG', 'KFT.LFT', 'Uric acid',
];

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const GPLAD_RANGE = Array.from({ length: 16 }, (_, i) => i); // 0-15
const DEFAULT_TEMPLATE_KEY = 'new';
const OBGYN_TEMPLATE_KEY = 'obgyn';
const INFERTILITY_TEMPLATE_KEY = 'infertility';
const PHYSICIAN_TEMPLATE_KEY = 'physician';
const PRESCRIPTION_TEMPLATE_KEYS = [DEFAULT_TEMPLATE_KEY, OBGYN_TEMPLATE_KEY, INFERTILITY_TEMPLATE_KEY, PHYSICIAN_TEMPLATE_KEY];

const SEMEN_OPTIONS = ['normal', 'abnormal forms', 'count ↓', 'motility'];
const NORMAL_ABNORMAL_OPTIONS = ['normal', 'abnormal'];

interface InventoryUsageItem {
  inventory_id: string;
  name: string;
  quantity: number;
  unit: string;
}

interface Drug {
  drug: string;
  frequency: string;
  period: string;
  timing: string;
  form?: string;
  route?: string;
  doseQty?: string;
  medicineDetails?: string;
}

/** Legacy stored dose labels → numeric options (1–4). */
const LEGACY_DOSE_QTY_TO_NUMBER: Record<string, string> = {
  قرص: '1',
  قرصين: '2',
  '3 أقراص': '3',
  '4 أقراص': '4',
};

function normalizeDrugRowForForm(row: Drug): Drug {
  const q = row.doseQty?.trim();
  if (q && LEGACY_DOSE_QTY_TO_NUMBER[q]) {
    return { ...row, doseQty: LEGACY_DOSE_QTY_TO_NUMBER[q] };
  }
  return row;
}

interface Receipt {
  _id?: string;
  drugModel: string;
  drugs: Drug[];
  notes: string;
  date: string;
  prescriptionTemplate?: string;
  investigations?: string[];
  nextVisitDate?: string | null;
  fetalAssessment?: FetalAssessment;
  showComplaint?: boolean;
  showDiagnosis?: boolean;
  diagnosis?: string;
}

interface PrescriptionQuickTemplate {
  template_id: string;
  name: string;
  prescriptionTemplate: string;
  diagnosis: string;
  drugs: Drug[];
  notes: string;
  investigations: string[];
}

interface Patient {
  _id: string;
  patient_name: string;
  patient_phone: string;
  visit_type?: string;
  age: string;
  address: string;
  receipts?: Receipt[];
  obgynHistory?: ObgynHistory;
  [key: string]: any;
}

interface ReceiptModalProps {
  visible: boolean;
  onCancel: () => void;
  patient: Patient | null;
  /** Called after save; pass server `patient` when available so UI can refresh obgynHistory etc. */
  onReceiptAdded: (updatedPatient?: any) => void;
  onSaveAndPrint?: (receipt: Receipt, printWindow: Window) => void;
  // Edit mode
  editMode?: boolean;
  receiptToEdit?: Receipt | null;
  visitId?: string | null;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({
  visible,
  onCancel,
  patient,
  onReceiptAdded,
  onSaveAndPrint,
  editMode = false,
  receiptToEdit = null,
  visitId = null,
}) => {
  const { t, isRTL } = useLanguage();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const saveAndPrintWindowRef = React.useRef<Window | null>(null);
  const [prescriptionTemplate, setPrescriptionTemplate] = useState<string>('new');
  const [autoCalculateEdd, setAutoCalculateEdd] = useState(true);
  const doctorContext = useDoctorContext();
  const doctorId = (doctorContext?.doctorId || localStorage.getItem('doctorId') || '').trim();
  const { selectedClinicId } = useClinicContext();
  const [ownerPrintSettings, setOwnerPrintSettings] = useState<any | null>(null);
  const ownPrintSettings = React.useMemo(
    () => doctorContext?.settings?.printSettings || {},
    [doctorContext?.settings?.printSettings]
  );
  const effectivePrintSettings = ownerPrintSettings || ownPrintSettings;
  const hasCustomPrescriptionStyle = Boolean(
    String((effectivePrintSettings as any)?.prescriptionCustomCss || '').trim() ||
    String((effectivePrintSettings as any)?.prescriptionCustomHtml || '').trim()
  );
  const { items: inventoryItems, recordUsage } = useInventoryContext();
  const [selectedInventoryItems, setSelectedInventoryItems] = useState<InventoryUsageItem[]>([]);
  const [selectedInvestigations, setSelectedInvestigations] = useState<string[]>([]);
  // Full per-doctor investigations list (base defaults + doctor's additions/removals)
  const [doctorInvestigations, setDoctorInvestigations] = useState<string[]>([]);
  // Manage investigations dialog
  const [manageInvVisible, setManageInvVisible] = useState(false);
  const [manageInvList, setManageInvList] = useState<string[]>([]);
  const [manageNewItem, setManageNewItem] = useState('');
  const [editingInvIdx, setEditingInvIdx] = useState<number | null>(null);
  const [editingInvValue, setEditingInvValue] = useState('');

  // OB/GYN history state (separate from form, pre-filled from patient)
  const [obgynHistory, setObgynHistory] = useState<ObgynHistory>({});
  const [fetalAssessment, setFetalAssessment] = useState<FetalAssessment>({});
  const [showComplaint, setShowComplaint] = useState(true);
  const [showDiagnosis, setShowDiagnosis] = useState(true);

  // Visit type resolved label (read-only display)
  const [resolvedVisitTypeLabel, setResolvedVisitTypeLabel] = useState<string>('');
  const [quickTemplates, setQuickTemplates] = useState<PrescriptionQuickTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateNameModal, setTemplateNameModal] = useState<{
    mode: 'create' | 'rename';
    template?: PrescriptionQuickTemplate;
  } | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateActionLoading, setTemplateActionLoading] = useState(false);

  // Suggestions
  const { diagnoses, complaints, saveDiagnosis, saveDrug, saveComplaint, filterDiagnoses, filterDrugs, getMedicineTemplate, getContraindications } = useDoctorSuggestions(doctorId);
  const watchedDiagnosis = Form.useWatch('diagnosis', form) as string | undefined;

  // Contraindication warnings per drug row index
  const [drugWarnings, setDrugWarnings] = useState<Record<number, string[]>>({});

  const CHIP_LIMIT = 15;

  // Drug names tracked outside the form entirely — this is the source of truth
  const [drugInputs, setDrugInputs] = useState<Record<number, string>>({});
  const [drugAutocompleteOpen, setDrugAutocompleteOpen] = useState<Record<number, boolean>>({});

  const isObgynFamilyTemplate = (template: string) =>
    template === OBGYN_TEMPLATE_KEY || template === INFERTILITY_TEMPLATE_KEY;

  const lastPrescriptionTemplateStorageKey = `lastPrescriptionTemplate_${doctorId || 'default'}`;
  const obgynPrintFieldsStorageKey = `obgynPrintFields_${doctorId || 'default'}`;

  React.useEffect(() => {
    let cancelled = false;

    const loadOwnerPrintSettings = async () => {
      setOwnerPrintSettings(null);
      if (!visible || !doctorId || !selectedClinicId || selectedClinicId === '__all_clinics__') return;
      if ((ownPrintSettings as any)?.useOwnerPrintSettings === false) return;

      try {
        const ownerDoctorId = await getClinicOwnerDoctorId(selectedClinicId, doctorId);
        if (!ownerDoctorId || ownerDoctorId === doctorId) return;

        const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SETTINGS(ownerDoctorId)}`);
        if (!cancelled) {
          setOwnerPrintSettings(response.data?.settings?.printSettings || null);
        }
      } catch (error) {
        console.warn('[ReceiptPatient] failed to load owner print settings:', error);
        if (!cancelled) setOwnerPrintSettings(null);
      }
    };

    loadOwnerPrintSettings();
    return () => {
      cancelled = true;
    };
  }, [visible, doctorId, selectedClinicId, ownPrintSettings]);

  const loadQuickTemplates = React.useCallback(async () => {
    if (!doctorId) return;
    try {
      setTemplatesLoading(true);
      const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.PRESCRIPTION_TEMPLATES(doctorId)}`);
      setQuickTemplates(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Failed to load prescription templates:', error);
      message.error(t('prescriptionTemplatesLoadFailed'));
    } finally {
      setTemplatesLoading(false);
    }
  }, [doctorId, t]);

  React.useEffect(() => {
    if (visible) loadQuickTemplates();
  }, [visible, loadQuickTemplates]);

  const setDrugInput = (index: number, value: string) => {
    setDrugInputs(prev => ({ ...prev, [index]: value }));
    const drugs = form.getFieldValue('drugs') || [];
    drugs[index] = { ...drugs[index], drug: value };
    form.setFieldsValue({ drugs });
  };

  const handleDrugNameKeyDown = (index: number, event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.key !== 'Enter') return;

    const hasSelectableSuggestion =
      drugAutocompleteOpen[index] &&
      filterDrugs(drugInputs[index] || '').length > 0 &&
      !event.shiftKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey;

    // Let AutoComplete handle Enter when its dropdown is open; otherwise keep
    // Enter as a newline inside the drug field and stop form submission.
    if (!hasSelectableSuggestion) {
      event.stopPropagation();
    }
  };

  /** Apply saved medicine defaults — uses per-field setFieldValue so Ant Design Form.List updates reliably */
  const applyDrugFromAutocomplete = (fieldIndex: number, medicineName: string) => {
    const trimmed = (medicineName || '').trim();
    if (!trimmed) return;
    setDrugInputs(prev => ({ ...prev, [fieldIndex]: trimmed }));
    form.setFieldValue(['drugs', fieldIndex, 'drug'], trimmed);
    const template = getMedicineTemplate(trimmed);
    if (!template) return;
    const keys: Array<'frequency' | 'period' | 'timing' | 'form' | 'route' | 'doseQty' | 'medicineDetails'> =
      ['frequency', 'period', 'timing', 'form', 'route', 'doseQty', 'medicineDetails'];
    keys.forEach((key) => {
      const v = template[key];
      if (v != null && String(v).length > 0) {
        form.setFieldValue(['drugs', fieldIndex, key], v);
      }
    });
  };

  // Reset / pre-fill form when modal opens
  React.useEffect(() => {
    if (visible) {
      setSelectedInventoryItems([]);
      setAutoCalculateEdd(true);

      // Load per-doctor investigations list (initialize from defaults if first time)
      const storageKey = `doctorInvestigations_${doctorId}`;
      const stored = localStorage.getItem(storageKey);
      setDoctorInvestigations(stored ? JSON.parse(stored) : [...OBGYN_INVESTIGATIONS]);

      if (editMode && receiptToEdit) {
        // Pre-fill all fields from the existing receipt
        const tmpl = receiptToEdit.prescriptionTemplate || 'new';
        setPrescriptionTemplate(tmpl);
        setSelectedInvestigations(receiptToEdit.investigations || []);
        setFetalAssessment(receiptToEdit.fetalAssessment || {});
        setShowComplaint(receiptToEdit.showComplaint !== false);
        setShowDiagnosis(receiptToEdit.showDiagnosis !== false);

        // Build drugInputs map from existing drugs
        const inputs: Record<number, string> = {};
        (receiptToEdit.drugs || []).forEach((d, i) => { inputs[i] = d.drug; });
        setDrugInputs(inputs);

        form.setFieldsValue({
          drugModel: tmpl,
          diagnosis: receiptToEdit.diagnosis || '',
          drugs: receiptToEdit.drugs?.length ? receiptToEdit.drugs.map(normalizeDrugRowForForm) : [{}],
          notes: receiptToEdit.notes || '',
          nextVisitDate: receiptToEdit.nextVisitDate ? dayjs(receiptToEdit.nextVisitDate) : null,
        });
      } else {
        // Add mode — fresh form
        const storedTemplate = localStorage.getItem(lastPrescriptionTemplateStorageKey) || '';
        const storedTemplateIsAvailable = storedTemplate !== PHYSICIAN_TEMPLATE_KEY || hasCustomPrescriptionStyle;
        const lastTemplate = PRESCRIPTION_TEMPLATE_KEYS.includes(storedTemplate) && storedTemplateIsAvailable
          ? storedTemplate
          : DEFAULT_TEMPLATE_KEY;
        form.resetFields();
        form.setFieldsValue({ drugModel: lastTemplate, drugs: [{}] });
        setPrescriptionTemplate(lastTemplate);
        setDrugInputs({});
        setSelectedInvestigations([]);
        setFetalAssessment({});
        try {
          const savedPrintFields = JSON.parse(localStorage.getItem(obgynPrintFieldsStorageKey) || '{}');
          setShowComplaint(savedPrintFields.showComplaint !== false);
          setShowDiagnosis(savedPrintFields.showDiagnosis !== false);
        } catch {
          setShowComplaint(true);
          setShowDiagnosis(true);
        }
      }

      // Pre-fill OB/GYN history from patient in both modes
      if (patient?.obgynHistory) {
        setObgynHistory(patient.obgynHistory);
      } else {
        setObgynHistory({});
      }

      // Resolve visit type display label from configured visit types
      if (patient?.visit_type) {
        const vt = patient.visit_type;
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctorId)}`)
          .then(res => {
            if (res.data?.success) {
              const types: any[] = res.data.data.visit_types || [];
              const match = types.find(t =>
                t.type_id === vt || t.name === vt || t.name_ar === vt
              );
              if (match) {
                setResolvedVisitTypeLabel(match.name_ar || match.name || vt);
              } else {
                setResolvedVisitTypeLabel(vt);
              }
            }
          })
          .catch(() => setResolvedVisitTypeLabel(patient.visit_type || ''));
      } else {
        setResolvedVisitTypeLabel('');
      }
    }
  }, [visible, form, patient, editMode, receiptToEdit, doctorId, lastPrescriptionTemplateStorageKey, obgynPrintFieldsStorageKey, hasCustomPrescriptionStyle]);

  const handleSubmit = async (values: any) => {
    if (!patient) return;

    const diagToSave = values.diagnosis?.trim() || '';
    const complaintToSave = values.complaint?.trim() || '';
    const drugNamesToSave = Object.values(drugInputs).filter((v): v is string => !!v?.trim());

    try {
      setIsLoading(true);

      const payload: any = {
        drugModel: values.drugModel || 'new',
        // The physician/PT sheet is a treatment program, not a medication list.
        // Do not persist the hidden default Form.List row for this layout.
        drugs: prescriptionTemplate === PHYSICIAN_TEMPLATE_KEY ? [] : (values.drugs || []),
        notes: values.notes || '',
        complaint: values.complaint || '',
        diagnosis: values.diagnosis || '',
        prescriptionTemplate,
        nextVisitDate: values.nextVisitDate ? values.nextVisitDate.format('YYYY-MM-DD') : null,
      };

      if (isObgynFamilyTemplate(prescriptionTemplate)) {
        payload.investigations = selectedInvestigations;
        payload.obgynHistory = obgynHistory;
        payload.fetalAssessment = fetalAssessment;
        payload.showComplaint = showComplaint;
        payload.showDiagnosis = showDiagnosis;
        localStorage.setItem(obgynPrintFieldsStorageKey, JSON.stringify({ showComplaint, showDiagnosis }));
      }

      let updatedPatientFromServer: any;
      let savedReceipt: Receipt | undefined;

      if (editMode && receiptToEdit?._id && visitId) {
        // ── Edit existing receipt ─────────────────────────────────
        const res = await axios.put(
          `${API.BASE_URL}${API.ENDPOINTS.UPDATE_RECEIPT(patient._id, visitId, receiptToEdit._id)}`,
          payload
        );
        updatedPatientFromServer = res.data?.patient;
        savedReceipt = res.data?.receipt;
      } else {
        // ── Add new receipt (original flow) ──────────────────────
        payload.date = dayjs().format('YYYY-MM-DD');
        // Pin the prescription to the visit that is open now. Offline replay
        // can run after the patient file changes, so "latest" is not stable.
        const patientVisits = Array.isArray(patient.visits) ? patient.visits : [];
        const targetVisit = patientVisits[patientVisits.length - 1];
        const targetVisitId = visitId || targetVisit?.visit_id || targetVisit?._id;
        if (targetVisitId) payload.visit_id = String(targetVisitId);
        const response = await axios.put(
          `${API.BASE_URL}${API.ENDPOINTS.PATIENT_BY_ID(patient._id)}`,
          payload,
          { offlineInventory: selectedInventoryItems.map(item => ({ inventory_id: item.inventory_id, quantity: item.quantity })) } as any
        );
        updatedPatientFromServer = response.data?.patient;
        savedReceipt = response.data?.receipt;

        if (patient.patient_phone) {
          sendReceiptNotificationToPatient({
            patientPhone: patient.patient_phone,
            patientName: patient.patient_name || '',
            patientId: patient._id || patient.patient_id || '',
            clinicId: selectedClinicId || patient.clinic_id || '',
            doctorId: doctorId || patient.doctor_id || '',
            doctorName: patient.doctor_name || '',
            receiptId: response.data?.patient?.visits?.[response.data.patient.visits.length - 1]?.receipts?.[response.data.patient.visits[response.data.patient.visits.length - 1].receipts.length - 1]?._id || ''
          }).catch((notificationError: any) => {
            console.error('Error sending receipt notification:', notificationError);
          });
        }
      }

      if (selectedInventoryItems.length > 0) {
        try {
          for (const item of selectedInventoryItems) {
            await recordUsage(
              item.inventory_id,
              patient._id,
              item.quantity,
              `Used during visit on ${dayjs().format('YYYY-MM-DD')}`
            );
          }
        } catch (inventoryError) {
          console.error('Error recording inventory usage:', inventoryError);
          message.warning(t('receiptSavedInventoryFailed'));
        }
      }

      message.success(editMode ? t('receiptUpdatedSuccess') : t('receiptSavedSuccess'));

      if (diagToSave) saveDiagnosis(diagToSave);
      if (complaintToSave) saveComplaint(complaintToSave);
      drugNamesToSave.forEach(name => saveDrug(name.trim()));

      form.resetFields();
      setDrugInputs({});
      setDrugWarnings({});
      setSelectedInvestigations([]);

      if (saveAndPrintWindowRef.current && onSaveAndPrint) {
        if (!savedReceipt) {
          const visits = updatedPatientFromServer?.visits || [];
          const receipts = visits.flatMap((visit: any) => visit.receipts || []);
          savedReceipt = editMode
            ? receipts.find((receipt: Receipt) => receipt._id === receiptToEdit?._id)
            : receipts[receipts.length - 1];
        }

        if (savedReceipt) {
          onSaveAndPrint(savedReceipt, saveAndPrintWindowRef.current);
        } else {
          saveAndPrintWindowRef.current.close();
          message.warning(t('prescriptionPrintFailed'));
        }
        saveAndPrintWindowRef.current = null;
      }

      onReceiptAdded(updatedPatientFromServer);
    } catch (error) {
      if (isProfileQueued(error)) {
        if (saveAndPrintWindowRef.current) { saveAndPrintWindowRef.current.close(); saveAndPrintWindowRef.current = null; }
        if (diagToSave) saveDiagnosis(diagToSave);
        if (complaintToSave) saveComplaint(complaintToSave);
        drugNamesToSave.forEach(name => saveDrug(name.trim()));
        form.resetFields();
        setDrugInputs({});
        setDrugWarnings({});
        setSelectedInvestigations([]);
        setSelectedInventoryItems([]);
        // The durable action is already in IndexedDB. Notify the parent just
        // like an online save so the modal closes and prescription history can
        // render that queued action immediately.
        onReceiptAdded();
        message.warning('Prescription saved on this device — pending sync.');
        return;
      }
      console.error('Error adding receipt:', error);
      if (saveAndPrintWindowRef.current) {
        saveAndPrintWindowRef.current.close();
        saveAndPrintWindowRef.current = null;
      }
      message.error(t('receiptSaveFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAndPrintClick = () => {
    if (!onSaveAndPrint) return;
    saveAndPrintWindowRef.current = openPrintWindow();
  };

  const handleFinishFailed = () => {
    if (saveAndPrintWindowRef.current) {
      saveAndPrintWindowRef.current.close();
      saveAndPrintWindowRef.current = null;
    }
  };

  const handleTemplateChange = (value: string) => {
    setPrescriptionTemplate(value);
    form.setFieldsValue({ drugModel: value });
    if (!editMode && PRESCRIPTION_TEMPLATE_KEYS.includes(value)) {
      localStorage.setItem(lastPrescriptionTemplateStorageKey, value);
    }
  };

  const reusableTemplatePayload = (name: string) => {
    const values = form.getFieldsValue();
    return {
      name: name.trim(),
      prescriptionTemplate,
      diagnosis: values.diagnosis || '',
      drugs: (values.drugs || []).map((drug: Drug) => ({ ...drug })),
      notes: values.notes || '',
      investigations: isObgynFamilyTemplate(prescriptionTemplate) ? [...selectedInvestigations] : [],
    };
  };

  const applyQuickTemplate = (template: PrescriptionQuickTemplate) => {
    const layout = PRESCRIPTION_TEMPLATE_KEYS.includes(template.prescriptionTemplate)
      ? template.prescriptionTemplate
      : DEFAULT_TEMPLATE_KEY;
    const drugs: Drug[] = template.drugs?.length
      ? template.drugs.map(drug => normalizeDrugRowForForm({ ...drug }))
      : [{ drug: '', frequency: '', period: '', timing: '' }];
    const inputs: Record<number, string> = {};
    drugs.forEach((drug, index) => { inputs[index] = drug.drug || ''; });

    setPrescriptionTemplate(layout);
    setSelectedInvestigations(template.investigations ? [...template.investigations] : []);
    setDrugInputs(inputs);
    setDrugWarnings({});
    form.setFieldsValue({
      drugModel: layout,
      diagnosis: template.diagnosis || '',
      drugs,
      notes: template.notes || '',
    });
    localStorage.setItem(lastPrescriptionTemplateStorageKey, layout);
    message.success(t('prescriptionTemplateApplied'));
  };

  const openCreateTemplateModal = () => {
    setTemplateName('');
    setTemplateNameModal({ mode: 'create' });
  };

  const openRenameTemplateModal = (template: PrescriptionQuickTemplate) => {
    setTemplateName(template.name);
    setTemplateNameModal({ mode: 'rename', template });
  };

  const saveTemplateNameModal = async () => {
    const name = templateName.trim();
    if (!name || !templateNameModal || !doctorId) return;
    try {
      setTemplateActionLoading(true);
      if (templateNameModal.mode === 'create') {
        await axios.post(
          `${API.BASE_URL}${API.ENDPOINTS.PRESCRIPTION_TEMPLATES(doctorId)}`,
          reusableTemplatePayload(name)
        );
        message.success(t('prescriptionTemplateSaved'));
      } else if (templateNameModal.template) {
        await axios.put(
          `${API.BASE_URL}${API.ENDPOINTS.PRESCRIPTION_TEMPLATE(doctorId, templateNameModal.template.template_id)}`,
          { ...templateNameModal.template, name }
        );
        message.success(t('prescriptionTemplateRenamed'));
      }
      setTemplateNameModal(null);
      await loadQuickTemplates();
    } catch (error) {
      console.error('Failed to save prescription template:', error);
      message.error(t('prescriptionTemplateSaveFailed'));
    } finally {
      setTemplateActionLoading(false);
    }
  };

  const updateQuickTemplate = (template: PrescriptionQuickTemplate) => {
    Modal.confirm({
      title: t('updatePrescriptionTemplate'),
      content: t('updatePrescriptionTemplateConfirm'),
      okText: t('save'),
      cancelText: t('cancel'),
      onOk: async () => {
        try {
          await axios.put(
            `${API.BASE_URL}${API.ENDPOINTS.PRESCRIPTION_TEMPLATE(doctorId, template.template_id)}`,
            reusableTemplatePayload(template.name)
          );
          message.success(t('prescriptionTemplateUpdated'));
          await loadQuickTemplates();
        } catch (error) {
          console.error('Failed to update prescription template:', error);
          message.error(t('prescriptionTemplateSaveFailed'));
        }
      },
    });
  };

  const deleteQuickTemplate = (template: PrescriptionQuickTemplate) => {
    Modal.confirm({
      title: t('deletePrescriptionTemplate'),
      content: t('deletePrescriptionTemplateConfirm'),
      okText: t('delete'),
      okButtonProps: { danger: true },
      cancelText: t('cancel'),
      onOk: async () => {
        try {
          await axios.delete(
            `${API.BASE_URL}${API.ENDPOINTS.PRESCRIPTION_TEMPLATE(doctorId, template.template_id)}`
          );
          message.success(t('prescriptionTemplateDeleted'));
          await loadQuickTemplates();
        } catch (error) {
          console.error('Failed to delete prescription template:', error);
          message.error(t('prescriptionTemplateDeleteFailed'));
        }
      },
    });
  };

  const quickTemplatesCard = (
    <Card
      size="small"
      title={t('savedPrescriptionTemplates')}
      extra={
        <Button size="small" icon={<SaveOutlined />} onClick={openCreateTemplateModal}>
          {t('saveAsPrescriptionTemplate')}
        </Button>
      }
      loading={templatesLoading}
      style={{ marginBottom: 12 }}
      bodyStyle={{ padding: quickTemplates.length ? '8px 10px' : '6px 10px' }}
    >
      {quickTemplates.length ? (
        <Space size={[6, 6]} wrap>
          {quickTemplates.map(template => (
            <Button.Group key={template.template_id}>
              <Button size="small" onClick={() => applyQuickTemplate(template)}>
                {template.name}
              </Button>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    { key: 'rename', icon: <EditOutlined />, label: t('renamePrescriptionTemplate') },
                    { key: 'update', icon: <ReloadOutlined />, label: t('updatePrescriptionTemplate') },
                    { type: 'divider' },
                    { key: 'delete', icon: <DeleteOutlined />, danger: true, label: t('deletePrescriptionTemplate') },
                  ],
                  onClick: ({ key }) => {
                    if (key === 'rename') openRenameTemplateModal(template);
                    if (key === 'update') updateQuickTemplate(template);
                    if (key === 'delete') deleteQuickTemplate(template);
                  },
                }}
              >
                <Button size="small" icon={<MoreOutlined />} aria-label={t('actions')} />
              </Dropdown>
            </Button.Group>
          ))}
        </Space>
      ) : (
        <span style={{ color: '#888', fontSize: 12 }}>{t('noSavedPrescriptionTemplates')}</span>
      )}
    </Card>
  );

  const handleAddInventoryItem = () => {
    const newItem: InventoryUsageItem = {
      inventory_id: '',
      name: '',
      quantity: 1,
      unit: ''
    };
    setSelectedInventoryItems([...selectedInventoryItems, newItem]);
  };

  const handleRemoveInventoryItem = (index: number) => {
    const updated = selectedInventoryItems.filter((_, i) => i !== index);
    setSelectedInventoryItems(updated);
  };

  const handleInventoryItemChange = (index: number, field: string, value: any) => {
    const updated = [...selectedInventoryItems];
    if (field === 'inventory_id') {
      const selectedItem = inventoryItems.find(item => item._id === value);
      if (selectedItem) {
        updated[index] = {
          inventory_id: value,
          name: selectedItem.name,
          quantity: 1,
          unit: selectedItem.unit
        };
      }
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setSelectedInventoryItems(updated);
  };

  const updateObgyn = (field: keyof ObgynHistory, value: any) => {
    setObgynHistory(prev => ({ ...prev, [field]: value }));
  };

  const updateFetalAssessment = (field: keyof FetalAssessment, value: string) => {
    setFetalAssessment(prev => ({ ...prev, [field]: value }));
  };

  const parseObgynDate = (value?: string) => {
    if (!value) return null;
    const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (match) {
      const enteredDay = Number(match[1]);
      const enteredMonth = Number(match[2]);
      const enteredYear = Number(match[3]);
      const fullYear = match[3].length === 2
        ? (enteredYear >= 69 ? 1900 + enteredYear : 2000 + enteredYear)
        : enteredYear;
      const parsed = dayjs(
        `${fullYear}-${String(enteredMonth).padStart(2, '0')}-${String(enteredDay).padStart(2, '0')}`
      );
      const matchesEnteredDate = parsed.isValid()
        && parsed.year() === fullYear
        && parsed.month() + 1 === enteredMonth
        && parsed.date() === enteredDay;
      return matchesEnteredDate ? parsed : null;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  };

  const updateEddFromLmp = (lmp: dayjs.Dayjs | null) => {
    updateObgyn('edd', lmp ? lmp.add(280, 'day').format('DD/MM/YYYY') : '');
  };

  // ─── Shared sections (complaint, diagnosis, drugs, inventory, notes) ───────

  const complaintDiagnosisSection = (
    <>
      <Form.Item label={t('complaint')}>
        <Form.Item name="complaint" noStyle>
          <TextArea placeholder={t('complaintPlaceholder')} rows={2} />
        </Form.Item>
        {complaints.slice(0, CHIP_LIMIT).length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {complaints.slice(0, CHIP_LIMIT).map((c) => (
              <Tag key={c} style={{ cursor: 'pointer', marginBottom: 0 }} onClick={() => form.setFieldsValue({ complaint: c })}>
                {c}
              </Tag>
            ))}
          </div>
        )}
      </Form.Item>

      <Form.Item label={t('diagnosis')}>
        <Form.Item name="diagnosis" noStyle>
          <AutoComplete
            options={filterDiagnoses(watchedDiagnosis || '')}
            filterOption={false}
            placeholder={t('diagnosisPlaceholder')}
            style={{ width: '100%' }}
          />
        </Form.Item>
        {diagnoses.slice(0, CHIP_LIMIT).length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {diagnoses.slice(0, CHIP_LIMIT).map((d) => (
              <Tag key={d} style={{ cursor: 'pointer', marginBottom: 0 }} onClick={() => form.setFieldsValue({ diagnosis: d })}>
                {d}
              </Tag>
            ))}
          </div>
        )}
      </Form.Item>
    </>
  );

  const drugDetailsCard = (
    <Card title={t('drugDetails')} bordered={true} size="small" style={{ marginBottom: '12px' }} bodyStyle={{ padding: '8px 10px' }}>
      <Form.List name="drugs">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <div key={key} style={{ marginBottom: '6px', borderBottom: '1px solid #f0f0f0', paddingBottom: '5px' }}>
                {/* Row 1: drug name + delete */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                  <div style={{ flex: 1 }}>
                    <AutoComplete
                      size="small"
                      value={drugInputs[name] ?? ''}
                      options={filterDrugs(drugInputs[name] || '')}
                      filterOption={false}
                      placeholder={t('drugNamePlaceholder')}
                      onDropdownVisibleChange={(open) => {
                        setDrugAutocompleteOpen(prev => ({ ...prev, [name]: open }));
                      }}
                      onInputKeyDown={(event) => handleDrugNameKeyDown(name, event)}
                      onChange={(val) => {
                        const v = typeof val === 'string' ? val : String(val ?? '');
                        setDrugInput(name, v);
                        // Clear warning while user is still typing
                        setDrugWarnings(prev => ({ ...prev, [name]: [] }));
                      }}
                      onSelect={(val: string) => {
                        setDrugAutocompleteOpen(prev => ({ ...prev, [name]: false }));
                        applyDrugFromAutocomplete(name, val);
                        const ci = getContraindications(val);
                        setDrugWarnings(prev => ({ ...prev, [name]: ci }));
                      }}
                      onBlur={() => {
                        const rowDrug = form.getFieldValue(['drugs', name, 'drug']);
                        const v = String(rowDrug ?? drugInputs[name] ?? '').trim();
                        if (v) {
                          applyDrugFromAutocomplete(name, v);
                          const ci = getContraindications(v);
                          setDrugWarnings(prev => ({ ...prev, [name]: ci }));
                        }
                      }}
                      style={{ width: '100%' }}
                    >
                      <TextArea
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        placeholder={t('drugNamePlaceholder')}
                        onKeyDown={(event) => handleDrugNameKeyDown(name, event)}
                      />
                    </AutoComplete>
                    {drugWarnings[name]?.length > 0 && (
                      <div style={{
                        marginTop: 3,
                        padding: '3px 8px',
                        background: '#fff7e6',
                        border: '1px solid #ffd591',
                        borderRadius: 4,
                        fontSize: 11,
                        color: '#d46b08',
                        lineHeight: 1.4,
                      }}>
                        ⚠️ <strong>Contraindications:</strong> {drugWarnings[name].join(' · ')}
                      </div>
                    )}
                  </div>
                  {(fields.length > 1 || editMode) && (
                    <Button size="small" type="text" danger onClick={() => remove(name)} style={{ flexShrink: 0, padding: '0 4px' }}>
                      ✕
                    </Button>
                  )}
                </div>
                {/* Row 2: frequency + period + timing */}
                <div style={{ display: 'flex', gap: '4px', marginBottom: '3px' }}>
                  <Form.Item {...restField} name={[name, 'frequency']} style={{ flex: 1, marginBottom: 0 }}>
                    <Select size="small" placeholder={t('frequency')}>
                      <Option value="_">_</Option>
                      <Option value="مرة واحدة">{t('frequencyOnce')}</Option>
                      <Option value="مره واحده عند اللزوم">{t('frequencyOnceAsNeeded')}</Option>
                      <Option value="مرتين">{t('frequencyTwice')}</Option>
                      <Option value="3 مرات">{t('frequency3Times')}</Option>
                      <Option value="4 مرات">{t('frequency4Times')}</Option>
                      <Option value="يومياً">{t('frequencyDaily')}</Option>
                      <Option value="أسبوعياً">{t('frequencyWeekly')}</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'period']} style={{ flex: 1, marginBottom: 0 }}>
                    <Select size="small" placeholder={t('period')}>
                      <Option value="يوميًا">{t('periodDaily')}</Option>
                      <Option value="1 يوم">{t('period1Day')}</Option>
                      <Option value="2 يوم">{t('period2Days')}</Option>
                      <Option value="3 يوم">{t('period3Days')}</Option>
                      <Option value="4 يوم">{t('period4Days')}</Option>
                      <Option value="5 يوم">{t('period5Days')}</Option>
                      <Option value="6 يوم">{t('period6Days')}</Option>
                      <Option value="أسبوع">{t('periodWeek')}</Option>
                      <Option value="أسبوعين">{t('period2Weeks')}</Option>
                      <Option value="3 اسابيع">{t('period3Weeks')}</Option>
                      <Option value="شهر">{t('periodMonth')}</Option>
                      <Option value="شهرياً">{t('periodMonthly')}</Option>
                      <Option value="علاج اسبوعي">{t('periodWeeklyTreatment')}</Option>
                      <Option value="علاج شهري">{t('periodMonthlyTreatment')}</Option>
                      <Option value="علاج كل 6 اشهر">{t('periodEvery6MonthsTreatment')}</Option>
                      <Option value="يوم بعد يوم">{t('periodDayAfterDay')}</Option>
                      <Option value="مرتان أسبوعياً">{t('period2TimesWeek')}</Option>
                      <Option value="3 مرات أسبوعياً">{t('period3TimesWeek')}</Option>
                      <Option value="جرعة تدريجية">{t('periodGradualTitration')}</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'timing']} style={{ flex: 1, marginBottom: 0 }}>
                    <Select size="small" placeholder={t('timing')}>
                      <Option value="_">_</Option>
                      <Option value="قبل النوم">{t('timingBeforeSleep')}</Option>
                      <Option value="بعد الأكل">{t('timingAfterMeals')}</Option>
                      <Option value="قبل الأكل">{t('timingBeforeMeals')}</Option>
                      <Option value="وسط الاكل">{t('timingDuringMeals')}</Option>
                      <Option value="بعد الفطار">{t('timingAfterBreakfast')}</Option>
                      <Option value="بعد الغداء">{t('timingAfterLunch')}</Option>
                      <Option value="قبل العشا">{t('timingBeforeDinner')}</Option>
                      <Option value="بعد العشاء">{t('timingAfterDinner')}</Option>
                    </Select>
                  </Form.Item>
                </div>
                {/* Row 3: form + route (optional) */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  <Form.Item {...restField} name={[name, 'form']} style={{ flex: 1, marginBottom: 0 }}>
                    <Select size="small" placeholder={t('drugForm')} allowClear>
                      <Option value="Tab">{t('drugFormTab')}</Option>
                      <Option value="Capsule">{t('drugFormCapsule')}</Option>
                      <Option value="Sachets">{t('drugFormSachets')}</Option>
                      <Option value="Amp">{t('drugFormAmp')}</Option>
                      <Option value="Patch">{t('drugFormPatch')}</Option>
                      <Option value="Cream">{t('drugFormCream')}</Option>
                      <Option value="Syrup">{t('drugFormSyrup')}</Option>
                      <Option value="Gel">{t('drugFormGel')}</Option>
                      <Option value="EyeDrops">{t('drugFormEyeDrops')}</Option>
                      <Option value="UnderTongue">{t('drugFormUnderTongue')}</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'route']} style={{ flex: 1, marginBottom: 0 }}>
                    <Select size="small" placeholder={t('drugRoute')} allowClear>
                      <Option value="Iv">{t('drugRouteIv')}</Option>
                      <Option value="Im">{t('drugRouteIm')}</Option>
                      <Option value="Oral">{t('drugRouteOral')}</Option>
                      <Option value="Spray">{t('drugRouteSpray')}</Option>
                      <Option value="Ointment">{t('drugRouteOintment')}</Option>
                      <Option value="Topical">{t('drugRouteTopical')}</Option>
                      <Option value="Sublingual">{t('drugRouteSublingual')}</Option>
                      <Option value="Lingual">{t('drugRouteLingual')}</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'doseQty']} style={{ flex: 1, marginBottom: 0 }}>
                    <Select size="small" placeholder={t('drugDoseQty')} allowClear>
                      <Option value="_">_</Option>
                      <Option value="1">{t('doseQtyOneTablet')}</Option>
                      <Option value="1.5">{t('doseQtyOneHalf')}</Option>
                      <Option value="2">{t('doseQtyTwoTablets')}</Option>
                      <Option value="2.5">{t('doseQtyTwoHalf')}</Option>
                      <Option value="3">{t('doseQtyThreeTablets')}</Option>
                      <Option value="4">{t('doseQtyFourTablets')}</Option>
                    </Select>
                  </Form.Item>
                </div>
                <Form.Item
                  {...restField}
                  name={[name, 'medicineDetails']}
                  style={{ marginBottom: 0, marginTop: '4px' }}
                >
                  <TextArea
                    rows={2}
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    placeholder={t('medicineDetailsPlaceholder')}
                  />
                </Form.Item>
              </div>
            ))}
            <Button size="small" type="dashed" onClick={() => add()} block style={{ marginTop: '6px' }}>
              {t('addAnotherDrug')}
            </Button>
          </>
        )}
      </Form.List>
    </Card>
  );

  const inventoryCard = (
    <Card title={t('suppliesUsed')} style={{ marginBottom: '16px' }} size="small">
      {selectedInventoryItems.map((item, index) => (
        <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
          <Select
            placeholder={t('selectSupplyPlaceholder')}
            style={{ flex: 2 }}
            value={item.inventory_id || undefined}
            onChange={(value) => handleInventoryItemChange(index, 'inventory_id', value)}
            showSearch
            optionFilterProp="children"
          >
            {inventoryItems.map(invItem => (
              <Option key={invItem._id} value={invItem._id}>
                {invItem.name} ({invItem.quantity} {invItem.unit})
              </Option>
            ))}
          </Select>
          <Input
            type="number"
            placeholder={t('quantity')}
            style={{ width: '100px' }}
            min={1}
            value={item.quantity}
            onChange={(e) => handleInventoryItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
          />
          <span style={{ minWidth: '50px' }}>{item.unit || ''}</span>
          <Button type="text" danger onClick={() => handleRemoveInventoryItem(index)}>
            {t('delete')}
          </Button>
        </div>
      ))}
      <Button type="dashed" onClick={handleAddInventoryItem} block style={{ marginTop: '8px' }}>
        {t('addSupply')}
      </Button>
    </Card>
  );

  // ─── OB/GYN history panel (left column) ──────────────────────────────────

  const obgynHistoryPanel = (
    <Card
      title={<span style={{ fontSize: 13 }}>{t('obstetricHistory')}</span>}
      bordered={true}
      size="small"
      style={{ marginBottom: 0, height: '100%' }}
      bodyStyle={{ padding: '8px 10px' }}
    >
      {/* Married since */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('marriedSince')}</div>
        <Input
          size="small"
          value={obgynHistory.marriedSince || ''}
          onChange={e => updateObgyn('marriedSince', e.target.value)}
          placeholder="e.g. 2015"
        />
      </div>

      {/* GPLAD - 5 dropdowns */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>{t('gplad')}</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {([
            ['G', 'gravidity', t('gravidity')],
            ['P', 'parity', t('parity')],
            ['L', 'living', t('living')],
            ['A', 'abortion', t('abortion')],
            ['D', 'deaths', t('deaths')],
          ] as [string, keyof ObgynHistory, string][]).map(([letter, field, label]) => (
            <div key={letter} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 'bold', color: '#d63384', marginBottom: 2 }}>{letter}</div>
              <Select
                size="small"
                style={{ width: '100%' }}
                value={obgynHistory[field] as number ?? null}
                onChange={val => updateObgyn(field, val)}
                placeholder="-"
              >
                {GPLAD_RANGE.map(n => (
                  <Option key={n} value={n}>{n}</Option>
                ))}
              </Select>
            </div>
          ))}
        </div>
      </div>

      {/* Delivery type + Since */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('deliveryType')}</div>
        <div style={{ display: 'flex', gap: 4 }}>
          <Select
            size="small"
            style={{ flex: 2 }}
            value={obgynHistory.deliveryType || undefined}
            onChange={val => updateObgyn('deliveryType', val)}
            allowClear
            placeholder="-"
          >
            <Option value="Normal">Normal</Option>
            <Option value="SC">SC (Cesarean)</Option>
          </Select>
          <Input
            size="small"
            style={{ flex: 1 }}
            value={obgynHistory.deliverySince || ''}
            onChange={e => updateObgyn('deliverySince', e.target.value)}
            placeholder={t('deliverySince')}
          />
        </div>
      </div>

      {/* LMP — auto-calculates EDD on valid date entry */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('lmp')}</div>
        <Space.Compact block>
          <Input
            size="small"
            value={obgynHistory.lmp || ''}
            onChange={event => {
              const value = event.target.value;
              updateObgyn('lmp', value);
              const parsed = parseObgynDate(value);
              if (autoCalculateEdd && parsed) updateEddFromLmp(parsed);
            }}
            onBlur={() => {
              const parsed = parseObgynDate(obgynHistory.lmp);
              if (parsed) {
                updateObgyn('lmp', parsed.format('DD/MM/YYYY'));
                if (autoCalculateEdd) updateEddFromLmp(parsed);
              }
            }}
            placeholder="DD/MM/YYYY"
            style={{ flex: 1 }}
          />
          <DatePicker
            size="small"
            value={parseObgynDate(obgynHistory.lmp)}
            onChange={date => {
              updateObgyn('lmp', date ? date.format('DD/MM/YYYY') : '');
              if (autoCalculateEdd) updateEddFromLmp(date);
            }}
            format="DD/MM/YYYY"
            placeholder={t('selectDate')}
            allowClear
            style={{ width: 145 }}
          />
        </Space.Compact>
      </div>

      {/* EDD - auto-filled from LMP or selectable manually */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 11, color: '#666' }}>{t('edd')}</span>
          <Checkbox
            checked={autoCalculateEdd}
            onChange={e => {
              const checked = e.target.checked;
              setAutoCalculateEdd(checked);
              if (checked) updateEddFromLmp(parseObgynDate(obgynHistory.lmp));
            }}
            style={{ fontSize: 11 }}
          >
            Auto
          </Checkbox>
        </div>
        <DatePicker
          size="small"
          value={parseObgynDate(obgynHistory.edd)}
          onChange={date => updateObgyn('edd', date ? date.format('DD/MM/YYYY') : '')}
          format="DD/MM/YYYY"
          placeholder="DD/MM/YYYY"
          allowClear
          disabled={autoCalculateEdd}
          style={{ width: '100%', background: autoCalculateEdd ? '#fff8fb' : undefined }}
        />
      </div>

      {/* Medications */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('obgynMedications')}</div>
        <TextArea
          size="small"
          rows={2}
          value={obgynHistory.medications || ''}
          onChange={e => updateObgyn('medications', e.target.value)}
        />
      </div>

      {/* Surgical history */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('surgicalHistory')}</div>
        <TextArea
          size="small"
          rows={2}
          value={obgynHistory.surgicalHistory || ''}
          onChange={e => updateObgyn('surgicalHistory', e.target.value)}
        />
      </div>

      {/* Husband details (OB/GYN & infertility) */}
      <div style={{ marginBottom: 8, padding: '6px 8px', background: '#fff8fb', borderRadius: 4, border: '1px solid #f9cfe0' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#d63384', marginBottom: 6 }}>{t('husbandDetailsSection')}</div>
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('husbandName')}</div>
          <Input
            size="small"
            value={obgynHistory.husbandName || ''}
            onChange={e => updateObgyn('husbandName', e.target.value)}
          />
        </div>
        <Row gutter={6} style={{ marginBottom: 6 }}>
          <Col span={8}>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('husbandAge')}</div>
            <Input
              size="small"
              value={obgynHistory.husbandAge || ''}
              onChange={e => updateObgyn('husbandAge', e.target.value)}
            />
          </Col>
          <Col span={8}>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('husbandJob')}</div>
            <Input
              size="small"
              value={obgynHistory.husbandJob || ''}
              onChange={e => updateObgyn('husbandJob', e.target.value)}
            />
          </Col>
          <Col span={8}>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('husbandResidency')}</div>
            <Input
              size="small"
              value={obgynHistory.husbandResidency || ''}
              onChange={e => updateObgyn('husbandResidency', e.target.value)}
            />
          </Col>
        </Row>
      </div>

      {/* Family history */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('familyHistory')}</div>
        <TextArea
          size="small"
          rows={2}
          value={obgynHistory.familyHistory || ''}
          onChange={e => updateObgyn('familyHistory', e.target.value)}
        />
      </div>

      {/* Consanguinity */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('consanguinity')}</div>
        <Select
          size="small"
          style={{ width: '100%' }}
          value={obgynHistory.consanguinity || undefined}
          onChange={val => updateObgyn('consanguinity', val)}
          allowClear
          placeholder="-"
        >
          <Option value="yes">نعم / Yes</Option>
          <Option value="no">لا / No</Option>
        </Select>
      </div>

      {/* Blood group */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('bloodGroup')}</div>
        <Select
          size="small"
          style={{ width: '100%' }}
          value={obgynHistory.bloodGroup || undefined}
          onChange={val => updateObgyn('bloodGroup', val)}
          allowClear
          placeholder="-"
        >
          {BLOOD_GROUPS.map(g => (
            <Option key={g} value={g}>{g}</Option>
          ))}
        </Select>
      </div>

      {/* Blood transfusion */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('bloodTransfusion')}</div>
        <Select
          size="small"
          style={{ width: '100%' }}
          value={obgynHistory.bloodTransfusion || undefined}
          onChange={val => updateObgyn('bloodTransfusion', val)}
          allowClear
          placeholder="-"
        >
          <Option value="yes">نعم / Yes</Option>
          <Option value="no">لا / No</Option>
        </Select>
      </div>

      {/* Blood pressure + Weight + Temperature */}
      <Row gutter={6} style={{ marginBottom: 8 }}>
        <Col span={8}>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('bloodPressure')}</div>
          <Input
            size="small"
            value={obgynHistory.bloodPressure || ''}
            onChange={e => updateObgyn('bloodPressure', e.target.value)}
            placeholder="120/80"
          />
        </Col>
        <Col span={8}>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('weight')}</div>
          <Input
            size="small"
            value={obgynHistory.weight || ''}
            onChange={e => updateObgyn('weight', e.target.value)}
            placeholder="kg"
          />
        </Col>
        <Col span={8}>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{t('temperature')}</div>
          <Input
            size="small"
            value={obgynHistory.temperature || ''}
            onChange={e => updateObgyn('temperature', e.target.value)}
            placeholder="°C"
          />
        </Col>
      </Row>

      <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>{t('fetalAssessment')}</div>
      <Row gutter={[6, 8]} style={{ marginBottom: 8 }}>
        {([
          ['efw', 'EFW'],
          ['fetalPulse', 'Fetal pulse'],
          ['es', 'ES'],
          ['as', 'AS'],
          ['gender', 'Gender'],
          ['liquor', 'Liquor'],
        ] as [keyof FetalAssessment, string][]).map(([field, label]) => (
          <Col xs={12} md={8} key={field}>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{label}</div>
            <Input
              size="small"
              value={fetalAssessment[field] || ''}
              onChange={event => updateFetalAssessment(field, event.target.value)}
              placeholder={label}
            />
          </Col>
        ))}
      </Row>

      {prescriptionTemplate === INFERTILITY_TEMPLATE_KEY && (
        <>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Semen</div>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={obgynHistory.semen || undefined}
              onChange={val => updateObgyn('semen', val)}
              allowClear
              placeholder="-"
            >
              {SEMEN_OPTIONS.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Hormones</div>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={obgynHistory.hormones || undefined}
              onChange={val => updateObgyn('hormones', val)}
              allowClear
              placeholder="-"
            >
              {NORMAL_ABNORMAL_OPTIONS.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>HSG</div>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={obgynHistory.hsg || undefined}
              onChange={val => updateObgyn('hsg', val)}
              allowClear
              placeholder="-"
            >
              {NORMAL_ABNORMAL_OPTIONS.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </div>
        </>
      )}

      <Form.Item name="notes" label={t('additionalNotes')} style={{ marginBottom: 0 }}>
        <TextArea placeholder={t('additionalNotesPlaceholder')} rows={3} />
      </Form.Item>
    </Card>
  );

  const obgynPrintFieldsCard = (
    <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: '8px 10px' }}>
      <Space wrap>
        <Checkbox checked={showComplaint} onChange={event => setShowComplaint(event.target.checked)}>
          {t('printComplaint')}
        </Checkbox>
        <Checkbox checked={showDiagnosis} onChange={event => setShowDiagnosis(event.target.checked)}>
          {t('printDiagnosis')}
        </Checkbox>
      </Space>
    </Card>
  );

  // ─── Investigation checkboxes (per-doctor: managed via dialog) ──────────

  const persistInvestigations = (list: string[]) => {
    setDoctorInvestigations(list);
    localStorage.setItem(`doctorInvestigations_${doctorId}`, JSON.stringify(list));
  };

  // ── Manage dialog helpers ────────────────────────────────────────────────
  const openManageDialog = () => {
    setManageInvList([...doctorInvestigations]);
    setManageNewItem('');
    setEditingInvIdx(null);
    setEditingInvValue('');
    setManageInvVisible(true);
  };

  const manageAddItem = () => {
    const trimmed = manageNewItem.trim();
    if (!trimmed || manageInvList.includes(trimmed)) { setManageNewItem(''); return; }
    setManageInvList(prev => [...prev, trimmed]);
    setManageNewItem('');
  };

  const manageRemoveItem = (idx: number) => {
    setManageInvList(prev => prev.filter((_, i) => i !== idx));
    if (editingInvIdx === idx) setEditingInvIdx(null);
  };

  const manageStartEdit = (idx: number) => {
    setEditingInvIdx(idx);
    setEditingInvValue(manageInvList[idx]);
  };

  const manageSaveEdit = (idx: number) => {
    const trimmed = editingInvValue.trim();
    if (!trimmed) { setEditingInvIdx(null); return; }
    setManageInvList(prev => prev.map((v, i) => i === idx ? trimmed : v));
    setEditingInvIdx(null);
  };

  const manageConfirm = () => {
    const oldNames = new Set(doctorInvestigations);
    const newNames = new Set(manageInvList);
    // Keep selected investigations that still exist; remap renamed ones
    const renamed: Record<string, string> = {};
    // Simple heuristic: if old list had same length and values differ at one position → renamed
    doctorInvestigations.forEach((old, i) => {
      if (manageInvList[i] && old !== manageInvList[i] && !newNames.has(old)) {
        renamed[old] = manageInvList[i];
      }
    });
    setSelectedInvestigations(prev =>
      prev
        .map(s => renamed[s] || s)
        .filter(s => newNames.has(s))
    );
    persistInvestigations(manageInvList);
    setManageInvVisible(false);
  };

  const investigationsSection = (
    <>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{t('investigations')}</span>
            <Tooltip title={isRTL ? 'إدارة الفحوصات' : 'Manage investigations'}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={openManageDialog}
                style={{ color: '#d63384', fontWeight: 600 }}
              >
                {isRTL ? 'تعديل' : 'Edit'}
              </Button>
            </Tooltip>
          </div>
        }
        bordered={true}
        size="small"
        style={{ marginBottom: '16px' }}
        bodyStyle={{ padding: '10px 12px' }}
      >
        <Checkbox.Group
          value={selectedInvestigations}
          onChange={vals => setSelectedInvestigations(vals as string[])}
          style={{ width: '100%' }}
        >
          <Row gutter={[4, 6]}>
            {doctorInvestigations.map(item => (
              <Col key={item} xs={12} sm={8} md={6} lg={4}>
                <Checkbox value={item} style={{ fontSize: 12 }}>{item}</Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Card>

      {/* ── Manage Investigations Dialog ─────────────────────────────────── */}
      <Modal
        title={
          <span style={{ color: '#d63384' }}>
            <EditOutlined style={{ marginLeft: 6, marginRight: 6 }} />
            {isRTL ? 'إدارة الفحوصات (خاصة بك)' : 'Manage Investigations (your list)'}
          </span>
        }
        open={manageInvVisible}
        onCancel={() => setManageInvVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setManageInvVisible(false)}>
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>,
          <Button
            key="save"
            type="primary"
            style={{ background: '#d63384', borderColor: '#d63384' }}
            onClick={manageConfirm}
          >
            {isRTL ? 'حفظ' : 'Save'}
          </Button>,
        ]}
        width={520}
        styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
        destroyOnClose
      >
        {/* Add new item row */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input
            placeholder={isRTL ? 'اسم الفحص الجديد' : 'New investigation name'}
            value={manageNewItem}
            onChange={e => setManageNewItem(e.target.value)}
            onPressEnter={manageAddItem}
            size="small"
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            onClick={manageAddItem}
            style={{ background: '#d63384', borderColor: '#d63384' }}
          >
            {isRTL ? 'إضافة' : 'Add'}
          </Button>
        </div>

        <List
          size="small"
          bordered
          dataSource={manageInvList}
          renderItem={(item, idx) => (
            <List.Item
              style={{ padding: '6px 10px' }}
              actions={[
                editingInvIdx === idx ? (
                  <Space size={4} key="edit-actions">
                    <Tooltip title={isRTL ? 'حفظ' : 'Save'}>
                      <Button
                        type="text"
                        size="small"
                        icon={<CheckOutlined style={{ color: '#52c41a' }} />}
                        onClick={() => manageSaveEdit(idx)}
                      />
                    </Tooltip>
                    <Tooltip title={isRTL ? 'إلغاء' : 'Cancel'}>
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined style={{ color: '#888' }} />}
                        onClick={() => setEditingInvIdx(null)}
                      />
                    </Tooltip>
                  </Space>
                ) : (
                  <Space size={4} key="view-actions">
                    <Tooltip title={isRTL ? 'تعديل' : 'Edit'}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined style={{ color: '#1677ff' }} />}
                        onClick={() => manageStartEdit(idx)}
                      />
                    </Tooltip>
                    <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => manageRemoveItem(idx)}
                      />
                    </Tooltip>
                  </Space>
                ),
              ]}
            >
              {editingInvIdx === idx ? (
                <Input
                  size="small"
                  value={editingInvValue}
                  onChange={e => setEditingInvValue(e.target.value)}
                  onPressEnter={() => manageSaveEdit(idx)}
                  autoFocus
                  style={{ width: '100%' }}
                />
              ) : (
                <span style={{ fontSize: 13 }}>{item}</span>
              )}
            </List.Item>
          )}
        />
        <div style={{ marginTop: 8, fontSize: 12, color: '#888', textAlign: isRTL ? 'right' : 'left' }}>
          {isRTL
            ? `${manageInvList.length} فحص — هذه القائمة خاصة بك فقط`
            : `${manageInvList.length} item(s) — this list is yours only, not shared with other doctors`}
        </div>
      </Modal>
    </>
  );

  return (
    <Modal
      title={editMode ? t('editPrescription') : t('addPrescription')}
      open={visible}
      onCancel={onCancel}
      footer={null}
      style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
      width={isObgynFamilyTemplate(prescriptionTemplate) ? 1050 : 720}
      destroyOnClose={true}
      styles={{ body: { maxHeight: '80vh', overflowY: 'auto', paddingTop: 12 } }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={handleFinishFailed}
        initialValues={{
          drugModel: 'new',
          drugs: [{}]
        }}
      >
        {/* Visit type (read-only) */}
        {resolvedVisitTypeLabel ? (
          <div style={{ marginBottom: 12, padding: '4px 10px', background: '#f6f8ff', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#888' }}>{t('visitTypeLabel')}:</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1677ff' }}>{resolvedVisitTypeLabel}</span>
          </div>
        ) : null}

        {!editMode && quickTemplatesCard}

        {/* Template selector */}
        <Form.Item
          name="drugModel"
          label={t('prescriptionTemplate')}
          rules={[{ required: true, message: t('selectPrescriptionTemplateRequired') }]}
        >
          <Select
            placeholder={t('selectPrescriptionTemplatePlaceholder')}
            onChange={handleTemplateChange}
          >
            <Option value="new">{t('newPrescription')}</Option>
            <Option value={OBGYN_TEMPLATE_KEY}>{t('obgynTemplate')}</Option>
            <Option value={INFERTILITY_TEMPLATE_KEY}>{t('infertilityTemplate')}</Option>
            {hasCustomPrescriptionStyle && (
              <Option value={PHYSICIAN_TEMPLATE_KEY}>{t('physicianTemplate')}</Option>
            )}
          </Select>
        </Form.Item>

        <Form.Item name="nextVisitDate" label={t('nextVisitDate')}>
          <DatePicker
            format="DD-MM-YYYY"
            placeholder={t('nextVisitDatePlaceholder')}
            allowClear
            style={{ width: '100%' }}
          />
        </Form.Item>

        {/* ── DEFAULT TEMPLATE (unchanged) ─────────────────────────────────── */}
        {!isObgynFamilyTemplate(prescriptionTemplate) && prescriptionTemplate !== PHYSICIAN_TEMPLATE_KEY && (
          <>
            {complaintDiagnosisSection}
            {drugDetailsCard}
            {inventoryCard}
            <Form.Item name="notes" label={t('additionalNotes')}>
              <TextArea placeholder={t('additionalNotesPlaceholder')} rows={3} />
            </Form.Item>
          </>
        )}

        {/* PHYSICIAN / PHYSICAL-THERAPY TEMPLATE */}
        {prescriptionTemplate === PHYSICIAN_TEMPLATE_KEY && (
          <>
            <Form.Item name="diagnosis" label={t('diagnosis')}>
              <AutoComplete
                options={filterDiagnoses(watchedDiagnosis || '')}
                filterOption={false}
                placeholder={t('diagnosisPlaceholder')}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item name="notes" label={t('treatmentProgram')}>
              <TextArea
                placeholder={t('treatmentProgramPlaceholder')}
                rows={5}
                dir="rtl"
                style={{ textAlign: 'right' }}
              />
            </Form.Item>
          </>
        )}

        {/* ── OB/GYN TEMPLATE ──────────────────────────────────────────────── */}
        {isObgynFamilyTemplate(prescriptionTemplate) && (
          <>
            {complaintDiagnosisSection}
            {obgynPrintFieldsCard}

            {/* Two-column layout: obstetric history (left) + drugs (right) */}
            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col xs={24} md={8}>
                {obgynHistoryPanel}
              </Col>
              <Col xs={24} md={16}>
                {drugDetailsCard}
              </Col>
            </Row>

            {/* Investigations below both columns */}
            {investigationsSection}

            {inventoryCard}
          </>
        )}

        <Form.Item>
          <Space.Compact block>
            <Button type="primary" htmlType="submit" loading={isLoading} style={{ flex: 1 }}>
              {editMode ? t('updatePrescription') : t('savePrescription')}
            </Button>
            {!editMode && onSaveAndPrint && (
              <Button
                htmlType="submit"
                icon={<PrinterOutlined />}
                loading={isLoading}
                onClick={handleSaveAndPrintClick}
                style={{ flex: 1 }}
              >
                {t('saveAndPrintPrescription')}
              </Button>
            )}
          </Space.Compact>
        </Form.Item>
      </Form>
      <Modal
        title={templateNameModal?.mode === 'rename' ? t('renamePrescriptionTemplate') : t('saveAsPrescriptionTemplate')}
        open={!!templateNameModal}
        onCancel={() => setTemplateNameModal(null)}
        onOk={saveTemplateNameModal}
        confirmLoading={templateActionLoading}
        okButtonProps={{ disabled: !templateName.trim() }}
        okText={t('save')}
        cancelText={t('cancel')}
        destroyOnClose
      >
        <Input
          value={templateName}
          onChange={event => setTemplateName(event.target.value)}
          onPressEnter={saveTemplateNameModal}
          placeholder={t('prescriptionTemplateNamePlaceholder')}
          autoFocus
          maxLength={100}
        />
      </Modal>
    </Modal>
  );
};

export default ReceiptModal;
